import {
  createContext,
  useContext,
  useEffect,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { staffMetricsKeys } from "@/features/metrics/staffMetrics";
import { checkinKeys } from "@/features/checkin/queries";
import type { ActiveCheckIn } from "@/features/checkin/types";
import { useOnlineStatus } from "./useOnlineStatus";
import { getActiveLocal, getIdMap, setActiveLocal } from "./db";
import {
  flush,
  getQueueSnapshot,
  initQueue,
  retryFailedPhotos,
  subscribeQueue,
  subscribeSynced,
} from "./queue";
import type { QueueSnapshot } from "./types";

interface OfflineContextValue extends QueueSnapshot {
  online: boolean;
}

const OfflineContext = createContext<OfflineContextValue>({
  online: true,
  pending: 0,
  failed: 0,
  flushing: false,
});

/** Re-flush this often (ms) while items are still pending and we're online,
 *  to clear transient failures without waiting for a connectivity event. */
const RETRY_INTERVAL_MS = 20_000;

export function OfflineProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const online = useOnlineStatus();
  const snapshot = useSyncExternalStore(subscribeQueue, getQueueSnapshot);

  // Load persisted counts on startup, then attempt a flush.
  useEffect(() => {
    void initQueue().then(() => flush());
  }, []);

  // Flush as soon as we regain connectivity. A real connectivity change also
  // gives failed photos a fresh start — they're idempotent and their failures
  // are usually the weak-signal attempt budget, not anything permanent.
  useEffect(() => {
    if (online) {
      void retryFailedPhotos();
      void flush();
    }
  }, [online]);

  // Background retry while work remains.
  useEffect(() => {
    if (!online || snapshot.pending === 0) return;
    const t = setInterval(() => void flush(), RETRY_INTERVAL_MS);
    return () => clearInterval(t);
  }, [online, snapshot.pending]);

  // When a queued write finally syncs, refresh the views that depend on it so
  // the optimistic state is reconciled with server truth.
  useEffect(() => {
    return subscribeSynced(async (item) => {
      if (item.kind === "metric_entry") {
        const activationId = (item.payload as { activationId?: string }).activationId;
        qc.invalidateQueries({ queryKey: staffMetricsKeys.byActivation(activationId) });
        qc.invalidateQueries({
          queryKey: ["admin", "metrics", "activation", activationId],
        });
        return;
      }

      if (item.kind === "checkin") {
        // Swap the optimistic "local:" id for the real one in place, so the
        // running timer doesn't flicker and a later checkout uses the real id.
        const { localCheckInId, staffId } = item.payload as {
          localCheckInId?: string;
          staffId?: string;
        };
        if (localCheckInId && staffId) {
          const realId = await getIdMap(localCheckInId);
          if (realId) {
            qc.setQueryData<ActiveCheckIn | null>(
              checkinKeys.active(staffId),
              (old) => (old && old.id === localCheckInId ? { ...old, id: realId } : old),
            );
            // Keep the persisted copy in step so a reload after this point
            // resolves to the real row, not the stale local id.
            const cur = (await getActiveLocal(staffId)) as ActiveCheckIn | null;
            if (cur && cur.id === localCheckInId) {
              await setActiveLocal(staffId, { ...cur, id: realId });
            }
          }
        }
        return;
      }

      if (item.kind === "checkout" || item.kind === "submit_log") {
        // Now that the shift is recorded server-side, pull fresh active +
        // hours so the UI matches the server.
        qc.invalidateQueries({ queryKey: checkinKeys.all });
      }
    });
  }, [qc]);

  return (
    <OfflineContext.Provider value={{ online, ...snapshot }}>
      {children}
    </OfflineContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOfflineQueue(): OfflineContextValue {
  return useContext(OfflineContext);
}
